import { execSync } from 'child_process';
import type { DetectedConnection } from './connect-detector.service.js';

export class ConnectExecService {
    runDbQuery(connection: DetectedConnection, sql: string): string {
        if (connection.runtime === 'mysql') {
            return this.runMysqlQuery(connection, sql);
        }
        if (connection.runtime === 'postgres') {
            return this.runPostgresQuery(connection, sql);
        }
        throw new Error(`Unsupported db runtime: ${connection.runtime}`);
    }

    runCacheGet(connection: DetectedConnection, key: string): string {
        return this.runRedisCli(connection, `GET ${this.escapeCliToken(key)}`);
    }

    runCacheSet(connection: DetectedConnection, key: string, value: string): string {
        return this.runRedisCli(
            connection,
            `SET ${this.escapeCliToken(key)} ${this.escapeCliToken(value)}`
        );
    }

    runCacheDelete(connection: DetectedConnection, key: string): string {
        return this.runRedisCli(connection, `DEL ${this.escapeCliToken(key)}`);
    }

    private runMysqlQuery(connection: DetectedConnection, sql: string): string {
        const args = [
            'mysql',
            connection.host ? `--host=${this.escapeCliToken(connection.host)}` : '',
            connection.port ? `--port=${connection.port}` : '',
            connection.username ? `--user=${this.escapeCliToken(connection.username)}` : '',
            connection.password ? `--password=${this.escapeCliToken(connection.password)}` : '',
            connection.database ? `--database=${this.escapeCliToken(connection.database)}` : '',
            `--execute=${this.escapeCliToken(sql)}`,
        ].filter(Boolean).join(' ');

        return execSync(args, { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] });
    }

    private runPostgresQuery(connection: DetectedConnection, sql: string): string {
        const connStr = connection.url || this.buildPostgresConnectionString(connection);
        const command = `psql ${this.escapeCliToken(connStr)} -c ${this.escapeCliToken(sql)}`;
        return execSync(command, {
            encoding: 'utf-8',
            stdio: ['pipe', 'pipe', 'pipe'],
            env: {
                ...process.env,
                PGPASSWORD: connection.password || process.env.PGPASSWORD,
            },
        });
    }

    private runRedisCli(connection: DetectedConnection, redisCommand: string): string {
        const args = [
            'redis-cli',
            connection.host ? `-h ${this.escapeCliToken(connection.host)}` : '',
            connection.port ? `-p ${connection.port}` : '',
            connection.password ? `-a ${this.escapeCliToken(connection.password)}` : '',
            '--raw',
            redisCommand,
        ].filter(Boolean).join(' ');

        return execSync(args, {
            encoding: 'utf-8',
            stdio: ['pipe', 'pipe', 'pipe'],
            env: {
                ...process.env,
                REDISCLI_AUTH: connection.password || process.env.REDISCLI_AUTH,
            },
        });
    }

    private buildPostgresConnectionString(connection: DetectedConnection): string {
        const user = encodeURIComponent(connection.username || 'postgres');
        const pass = connection.password ? `:${encodeURIComponent(connection.password)}` : '';
        const host = connection.host || 'localhost';
        const port = connection.port || 5432;
        const db = connection.database || 'postgres';
        return `postgresql://${user}${pass}@${host}:${port}/${db}`;
    }

    private escapeCliToken(input: string): string {
        return `'${input.replace(/'/g, `'\\''`)}'`;
    }
}

